import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { partition } from "lodash";

import SearchPopover, { EmphasizedElement } from "../SearchPopover";
import SearchPopoverItem from "../SearchPopoverItem";
import SearchPopoverHeading from "../SearchPopoverHeading";
import { Button } from "../../wombat-ui";
import { CloseIcon } from "../../icons";
import { useDebounce, useFirstMountState } from "react-use";
import useTranslations from "../../../hooks/useTranslations";
import SearchInput from "../SearchInput";
import { TailSpinLoader } from "../../loaders/TailSpinLoader";
import GroupTag from "../../groups/GroupTag";
import UserAvatar from "../avatars/UserAvatar";
import { useGetUsersLazyQuery, UserSummaryFragment } from '../../../graphql/generated/definitions';

const UserSearchItem: React.FC<{
  user: UserSummaryFragment;
  emphasized?: boolean;
  baseId?: string;
}> = ({ user, baseId, emphasized = false }) => (
  <SearchPopoverItem
    id={`${baseId}${user.id}${emphasized ? "-emphasized" : ""}`}
  >
    <div className="flex space-x-2">
      <div className="flex-shrink-0">
        <UserAvatar size={4} src={user?.avatar} alt={user?.name} />
      </div>
      <div className="flex-1 text-left">
        <p>{user?.name}</p>
        {user.groups.length > 0 && (
          <div className="flex flex-nowrap overflow-hidden space-x-1 pt-0.5">
            {user.groups.map(
              (group, groupIndex) =>
                groupIndex < 2 && (
                  <GroupTag disabled group={group} scale="sm" key={group.id} />
                )
            )}
          </div>
        )}
      </div>
    </div>
  </SearchPopoverItem>
);

export const DefaultSelectedUser: React.FC<{
  user: UserSummaryFragment;
  handleUserDelete?: (id: string) => void;
  removable?: boolean;
  baseId?: string;
}> = ({ user, baseId, handleUserDelete, removable = true }) => {
  const t = useTranslations();

  return (
    <div
      key={user.id}
      className="flex items-center justify-between w-full max-w-lg p-2 rounded-sm bg-light-0 lg:max-w-sm"
    >
      <div className="flex items-center">
        <UserAvatar src={user.avatar} alt="" size={6} />
        <p className="ml-4 text-sm font-medium text-dark-100">{user.name}</p>
      </div>
      {removable && handleUserDelete && (
        <Button
          scale="xs"
          variant="ghost"
          icon={CloseIcon}
          id={`${baseId}${user.id}-user-remove`}
          onClick={() => handleUserDelete(user.id)}
        >
          <span className="sr-only">
            {t("forms.actions.removeEntity", { entity: user.name })}
          </span>
        </Button>
      )}
    </div>
  );
};

type UserSearchProps = {
  defaultValue?: string[];
  onSelect?: (users: UserSummaryFragment) => void;
  onRemove?: (users: UserSummaryFragment) => void;
  onChange?: (users: UserSummaryFragment[]) => void;
  renderSelectedItem?: (
    user: UserSummaryFragment,
    index: number,
    onRemove: () => void,
    isLast: boolean
  ) => React.ReactNode | React.ReactNode[];
  emphasized?: EmphasizedElement<string>[];
  baseId?: string;
  children?: (props: {
    users: UserSummaryFragment[];
    handleUserDelete: (id: string) => void;
  }) => React.ReactNode;
};

const UserSearch: React.FC<UserSearchProps> = ({
  onSelect,
  onRemove,
  onChange,
  defaultValue = [],
  renderSelectedItem,
  emphasized = [],
  baseId,
  children,
}) => {
  const isFirstMount = useFirstMountState();
  const t = useTranslations();

  const [users, setUsers] = useState<UserSummaryFragment[]>([]);
  const [emphasizedUsers, setEmphasizedUsers] = useState<
    EmphasizedElement<UserSummaryFragment>[]
  >([]);
  const [isPopoverOpen, togglePopover] = useState(false);

  const [
    getUsers,
    { data: usersData, loading: usersLoading },
  ] = useGetUsersLazyQuery({
    variables: { sortBy: "name" }
  });

  const [
    getDefaultUsers,
    { data: defaultUsersData, loading: defaultUsersLoading },
  ] = useGetUsersLazyQuery({
    variables: { sortBy: "name" }
  });

  const [
    getEmphasizedUsers,
    { data: emphasizedUsersData },
  ] = useGetUsersLazyQuery({
    variables: { sortBy: "name" }
  });

  useEffect(() => {
    if (isFirstMount) {
      getUsers({
        variables: { per: 20, direction: "ASC" },
      });
      if (defaultValue.length > 0 || emphasized.length > 0)
        getDefaultUsers({
          variables: { ids: [...defaultValue], direction: "ASC" },
        });
    }
    // This is used because lazyQuery doesn't return a Promise
    if (defaultUsersData) {
      setUsers(
        defaultUsersData.users.collection.filter(({ id }) =>
          defaultValue.includes(id)
        )
      );
    }
  }, [isFirstMount, defaultUsersData]);

  useEffect(() => {
    if (emphasized.length > 0)
      getEmphasizedUsers({
        variables: {
          ids: emphasized.flatMap(({ elements }) => elements),
          direction: "ASC",
        },
      });
  }, [emphasized]);

  useEffect(() => {
    if (emphasizedUsersData) {
      setEmphasizedUsers(
        emphasized.map(({ elements, ...rest }) => ({
          ...rest,
          elements: emphasizedUsersData.users.collection.filter(({ id }) =>
            elements.includes(id)
          ),
        }))
      );
    }
  }, [emphasizedUsersData, users]);

  const handleUserDelete = useCallback(
    (id: string) => {
      const [newState, usersToRemove] = partition(
        users,
        ({ id: otherId }) => id !== otherId
      );

      if (onRemove) {
        onRemove(usersToRemove[0]);
      }

      if (onChange) {
        onChange(newState);
      }

      setUsers(newState);
    },
    [users]
  );

  const filteredUsers = useMemo(() => {
    return usersData?.users.collection.filter(
      ({ id: retrievedId }) => !users.find(({ id }) => retrievedId === id)
    ) || [];
  }, [usersData, users]);

  const filteredEmphasizedUsers = useMemo(() => {
    return emphasizedUsers.map(({ elements, ...rest }) => ({
      ...rest,
      elements: elements.filter(
        ({ id: retrievedId }) => !users.find(({ id }) => retrievedId === id)
      ),
    }));
  }, [users, emphasizedUsers]);

  const [ value, setValue ] = useState("");
  
  useDebounce(() => {
    getUsers({ variables: { q: value, direction: "ASC", per: 20 } })
  }, 500, [value]);

  const handleItemSelected = useCallback(
    (item: UserSummaryFragment) => {
      togglePopover(false);

      const newUsers = [...users, item];
      setUsers(newUsers);
      if (onSelect) {
        onSelect(item);
      }
      if (onChange) {
        onChange(newUsers);
      }
      setValue("");
    },
    [users]
  );

  return (
    <div className="flex flex-col gap-y-2">
      <SearchPopover
        onItemSelected={handleItemSelected}
        title={
          <SearchPopoverHeading>
            {t("reviews.users") as string}
          </SearchPopoverHeading>
        }
        hasSearch={false}
        open={isPopoverOpen}
        button={
          <div>
            <SearchInput
              liveSearch
              id={`${baseId}search`}
              onFocus={() => togglePopover(true)}
              onBlur={() => togglePopover(false)}
              placeholder={t("users.searchPlaceholder") as string}
              value={value}
              onChange={setValue}
            />
          </div>
        }
        placeholder={t("users.searchPlaceholder") as string}
        emphasized={filteredEmphasizedUsers}
        items={filteredUsers}
        loading={!usersData || usersLoading}
        renderItem={(item: UserSummaryFragment, emphasized) => (
          <UserSearchItem user={item} baseId={baseId} emphasized={emphasized} />
        )}
      />
      <div className="flex flex-wrap gap-x-4 gap-y-3">
        {defaultValue && defaultUsersLoading && (
          <div className="flex flex-col items-center justify-center w-full h-48 max-w-lg lg:max-w-sm">
            <TailSpinLoader className="w-6 h-6" />
          </div>
        )}
        {children
          ? children({ users, handleUserDelete })
          : users.map((user, index) => {
              if (!renderSelectedItem)
                return (
                  <DefaultSelectedUser
                    key={user.id}
                    user={user}
                    baseId={baseId}
                    handleUserDelete={handleUserDelete}
                  />
                );
              return renderSelectedItem(
                user,
                index,
                () => handleUserDelete(user.id),
                index === users.length - 1
              );
            })}
      </div>
    </div>
  );
};

export default React.memo(UserSearch);
